import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { camelizeKeys } from "humps";

import api from "@/api/base";

/**
 * Violation-remediation matter read API (PR-1 endpoint, PR-2 consumer).
 *
 * `GET /api/tracking/portal/buildings/<id>/violation-matters/`
 *
 * The endpoint is gated by the `violation_remediation` product flag
 * (house default TRUE) on the BE. When the flag is OFF the GET returns
 * 404 — the query errors, `data` stays undefined, and every PR-2
 * surface that consumes it renders nothing. That is how the flag
 * "manifests on the wire" with zero extra FE plumbing (identical to the
 * `usePathwayChangesQuery` contract). The building-detail penalty
 * section therefore renders EXACTLY as today when the flag is off.
 *
 * Wire shape note (camelCase axios boundary — see `api/base.ts`):
 * `humps.camelizeKeys` runs on every JSON response, INCLUDING the keys
 * nested inside `requirementsProfile` and the `charges` JSONField. So
 * the BE `next_deadline_kind` arrives as `nextDeadlineKind`,
 * `requirements_profile.certifying_authority` as
 * `requirementsProfile.certifyingAuthority`, etc. Values are NOT
 * transformed — `lifecycleStatus === "DEFAULTED"` keeps its raw string.
 */

// ---------------------------------------------------------------------------
// Constant unions — mirror the BE service-validated CharField constants
// (`tracking/models_violation.py`). Kept as string-literal unions so a
// stray status typo is a compile error at the derivation call sites.
// ---------------------------------------------------------------------------

export type ViolationLifecycleStatus =
  | "OPEN"
  | "SCHEDULED"
  | "DEFAULTED"
  | "ADJUDICATED_OWING"
  | "STIPULATED_PARTIAL"
  | "RESOLVED_PAID"
  | "RESOLVED_DISMISSED"
  | "WRITTEN_OFF"
  | "NOT_APPLICABLE"
  | "UNCLASSIFIED";

export type ViolationCertificationStatus =
  | "NOT_REQUIRED"
  | "UNKNOWN"
  | "DUE"
  | "FILED"
  | "ACCEPTED";

export type ViolationAttribution =
  | "OWNER"
  | "TENANT_OR_THIRD_PARTY"
  | "NEEDS_TRIAGE";

/**
 * `attribution_posture` — the machine-readable, DERIVED posture the read API
 * emits per matter (BE: `matter_attribution_posture`). The owner-facing words
 * are counsel-gated (`attributionVerifyCopy` stays null), so the FE branches
 * on THIS enum.
 *
 * ⚠️ Contract note (verified against `services_violation_attribution.py`
 * 2026-07-29): these are the POSTURE values (OWNER_SIDE / UNDER_REVIEW /
 * PRIOR_OWNER_VERIFY / NOT_YOURS) — a DIFFERENT vocabulary from the
 * `attribution` FIELD (OWNER / TENANT_OR_THIRD_PARTY / NEEDS_TRIAGE). The
 * posture is what cleanly separates PRIOR_OWNER_VERIFY (a subset of the
 * NEEDS_TRIAGE attribution whose respondent PREDATES ownership) from plain
 * UNDER_REVIEW — a split `attribution` alone cannot express.
 */
export type AttributionPosture =
  | "OWNER_SIDE"
  | "UNDER_REVIEW"
  | "PRIOR_OWNER_VERIFY"
  | "NOT_YOURS";

/**
 * `attribution_split` — the FR-14 move-never-delete partition of the owed
 * OATH dollars. The BE owns ALL of this math (read-through
 * `score_exposure_usd`, the SAME `max(0,·)` floor the hero uses); the FE only
 * READS it.
 *
 * Invariant the BE pins: `yourEntities + underReview + notYours ==
 * compositeOathOwed` (== the composite's OATH subset). `owedTotal ==
 * yourEntities + underReview` (confirmed third-party has LEFT the owed hero).
 * All amounts are wire Decimal strings ("117075" / "1580.00").
 *
 * Camelized from the BE keys (`your_entities`, `not_yours`, `prior_owner`,
 * `composite_oath_owed`, `verify_copy`, …) by the axios boundary.
 */
export interface AttributionSplitBucket {
  amount: string;
  count: number;
}
export interface AttributionSplitNotYours extends AttributionSplitBucket {
  /** Always "NOT_YOURS". */
  posture: string;
}
export interface AttributionSplitPriorOwner extends AttributionSplitBucket {
  /** Always "PRIOR_OWNER_VERIFY". */
  posture: string;
  /** Counsel-gated lien wording — null until counsel approves it (CO-R5). */
  verifyCopy: string | null;
}
export interface AttributionSplit {
  /** yourEntities + underReview — what STAYS in the owed hero. */
  owedTotal: string;
  yourEntities: AttributionSplitBucket;
  underReview: AttributionSplitBucket;
  /** Confirmed third-party — LEAVES the owed hero (the "$X isn't yours" line). */
  notYours: AttributionSplitNotYours;
  /** Subset of underReview (respondent PREDATES) — dollars stay IN owed. */
  priorOwner: AttributionSplitPriorOwner;
  /** yourEntities + underReview + notYours — the full OATH subset partitioned. */
  compositeOathOwed: string;
}

export type ViolationRespondentResolution =
  | "UNRESOLVED"
  | "MATCHED_OWNER"
  | "MATCHED_AGENT"
  | "MATCHED_OTHER"
  | "PREDATES_OWNERSHIP";

/** `next_deadline_kind` — BE emits only these three (`""` = no clock). */
export type ViolationDeadlineKind = "" | "REOPEN" | "APPEAL";

/**
 * `requirements_profile` — the charge-code-keyed rulebook resolved to a
 * flat dict per matter. `verification` is `"unverified"` for ALL 12
 * codes today; a parallel primary-source-verification task flips codes
 * to `"verified"` and the same UI then renders per-agency specifics.
 * The UI MUST render conservative "under review" copy whenever
 * `verification !== "verified"` (or the agency/authority is unknown).
 */
export interface ViolationRequirementsProfile {
  agency: string | null;
  certificationAvailable: boolean | null;
  cureAvailable: boolean | null;
  certifyingAuthority: string | null;
  certifyingPortal: string | null;
  certifyingForm: string | null;
  citation: string | null;
  reopenWindowDays: number | null;
  appealWindowDays: number | null;
  verification: string | null;
  /**
   * PR-V5 (single-violation drawer) — the DOB hazard class ("Class 1
   * (immediately hazardous) — inferred") + the profile's human label ("DOB
   * elevator — failure to maintain, Class 1 (B151)"). Both absent/`null` on the
   * conservative default profile (an unmapped/Miscellaneous code carries
   * neither), so they are optional. Camelized from `hazard_class` / `label`.
   */
  hazardClass?: string | null;
  label?: string | null;
}

/**
 * A ticket's citation state. `cityHasNone` is terminal and nobody's fault;
 * `fetchFailed` and `notFetched` are ours. Never collapse them into a bare
 * "missing" — "1 of 5" then reads as "the city only has one".
 */
export type CitationState =
  | "on_file"
  | "city_has_none"
  | "fetch_failed"
  | "not_fetched";

export interface ViolationMatter {
  id: string;
  buildingId: string;
  ticketNumber: string;
  chargeCode: string;
  /** Every populated charge tuple (charge_1..4) — BE-R3 multi-charge. */
  charges: unknown[];
  description: string;
  issuingAgency: string;
  /** The short code (BE `serialize_matter`, `violation.py:849`). Read it
   *  THROUGH `agencyCode`. OPTIONAL like `citationState`: a payload persisted
   *  before 2026-09-24 carries none, so no `CACHE_SCHEMA_VERSION` bump. */
  issuingAgencyCode?: string;
  lifecycleStatus: ViolationLifecycleStatus;
  certificationStatus: ViolationCertificationStatus;
  attribution: ViolationAttribution;
  respondentResolution: ViolationRespondentResolution;
  respondentName: string | null;
  /**
   * FR-14 attribution layer (PR-4). The DERIVED posture the per-row treatment
   * branches on; `attributionManual` is true once a verdict has been recorded;
   * `respondentVerifyRequired` gates the prior-owner "verify" note;
   * `attributionVerifyCopy` is the counsel-gated lien sentence (null today).
   */
  attributionPosture: AttributionPosture;
  attributionManual: boolean;
  respondentVerifyRequired: boolean;
  attributionVerifyCopy: string | null;
  hearingDate: string | null;
  hearingStatus: string | null;
  hearingResult: string | null;
  /** Feed decision date — the reopen/appeal-clock ESTIMATE basis (`~`). */
  decisionDate: string | null;
  /** Thread-firmed mailing date — firms the estimate (drops the `~`). */
  decisionMailedDate: string | null;
  nextDeadlineAt: string | null;
  nextDeadlineKind: ViolationDeadlineKind;
  profileVersion: string;
  requirementsProfile: ViolationRequirementsProfile;
  /** Live dollar read-through (BE-B1). null → "no longer on the city ledger". */
  onCityLedger: boolean;
  exposureUsd: string | null;
  /** Resolved composite `row_id` — the join key back to a penalty row. */
  rowId: string | null;
  rowSource: string | null;
  primarySourceUrl: string | null;
  /** Whether this ticket's city citation PDF is downloadable, and if not, why.
   *  Server-derived from ONE shared map (`citation_state_by_matter`) so the
   *  cluster card and the case block can never disagree about a ticket.
   *  OPTIONAL for stale-persister tolerance: absent → no download affordance. */
  citationState?: CitationState;
  /** `exposure_composer.BUCKETS`, or not_counted (composed, on no line); null only at one_number v1. */
  bucket?:
    | "yours"
    | "under_review"
    | "not_yours"
    | "dob_direct"
    | "oath_no_matter"
    | "counted_elsewhere"
    | "corrected"
    | "not_counted"
    | null;
  /** The matter's share of the headline, "0.00" outside it; null exactly when `bucket` is. */
  headlineUsd?: string | null;
}

export interface ViolationMattersResponse {
  buildingId: string;
  asOf: string | null;
  oathSource: string | null;
  count: number;
  matters: ViolationMatter[];
  /** FR-14 move-never-delete partition of the owed OATH dollars (PR-4). */
  attributionSplit: AttributionSplit;
}

export const fetchBuildingViolationMatters = (
  buildingId: string,
): Promise<ViolationMattersResponse> =>
  api
    .get(`tracking/portal/buildings/${buildingId}/violation-matters/`)
    .then((res) => res.data);

export const violationMattersQueryKey = (buildingId: string | undefined) =>
  ["building", buildingId, "violation-matters"] as const;

/**
 * useBuildingViolationMattersQuery — building-scoped managed matters.
 *
 * Failure mode (by design): a 404 (flag off / no BIN / RLS mismatch)
 * leaves `data` undefined and `isSuccess` false. Callers MUST treat the
 * absence of `data.matters` as "no augmentation" so the penalty section
 * degrades to its pre-PR-2 appearance. `retry: false` because a 404 is
 * an expected steady-state (flag-scoped rollout), not a transient error
 * worth hammering three times.
 *
 * 5-minute `staleTime` mirrors the sibling penalty-exposure query so the
 * two reads stay coherent within a page visit.
 */
export const useBuildingViolationMattersQuery = (
  buildingId: string | undefined,
) =>
  useQuery<ViolationMattersResponse>({
    queryKey: violationMattersQueryKey(buildingId),
    enabled: !!buildingId && buildingId.length > 0,
    queryFn: () => fetchBuildingViolationMatters(buildingId as string),
    staleTime: 1000 * 60 * 5,
    retry: false,
  });

// ===========================================================================
// PR-V5 — single-violation DETAIL (screen 3, the drawer).
//
// `GET /api/tracking/portal/buildings/<id>/violation-matters/<matterId>/`
//
// The full `ViolationMatter` list shape PLUS the drawer-only extras the BE
// serves from a single-matter read (they would be an N+1 in the list loop):
// the issued date, statute, citation image URL, raw inspector narrative, and
// the issued -> hearing/defaulted -> docketed milestone timeline. Same flag
// contract as the list query — flag OFF (or a non-existent/other-tenant matter)
// → 404 → `data` stays undefined → the drawer renders its empty state.
//
// Wire note (camelCase axios boundary): `issued_date` → `issuedDate`,
// `citation_image_url` → `citationImageUrl`, `inspector_narrative` →
// `inspectorNarrative`; each timeline item `{kind,label,at}` is deep-camelized
// (its keys are already single-word, VALUES untouched — `kind` stays
// "issued"/"hearing"/"docketed"/"decision").
// ===========================================================================

/** A milestone node on the per-violation timeline (BE `build_matter_timeline`).
 *  `kind` is the milestone class; the FE caps the rail with a live "Today". */
export type ViolationTimelineKind =
  | "issued"
  | "hearing"
  | "docketed"
  | "decision";

export interface ViolationTimelineEntry {
  kind: ViolationTimelineKind;
  /** Lifecycle-aware label off the wire ("Defaulted at hearing" / "Docketed"). */
  label: string;
  /** ISO "YYYY-MM-DD" — the milestone date (materialized, never guessed). */
  at: string;
}

/**
 * PR-C3 (citation-ingestion CIT-9) + DOB cure-window (compliance relabel) — the
 * BE's ONE read-side cert-axis derivation (`derive_cert_axis_state`),
 * D-2 customer-gated.
 *
 *   - `source === "citation_document"` (FDNY) appears ONLY when a staff-VERIFIED
 *     extract carries a confident compliance date → the "Certify — overdue
 *     since …" / "due by …" cert-deadline copy.
 *   - `source === "dob_cure_window"` is the DOB "CURE DATE (Zero Penalty
 *     Option)" — a penalty-WAIVER window, NOT a certification deadline. Its
 *     `state` is `"cure_lapsed"` (cure date in the PAST) or `"cure_open"` (cure
 *     date in the FUTURE); the drawer renders cure-window copy and must NEVER
 *     borrow the "Certify — overdue" wording.
 *
 * `state`/`source` are field VALUES — the camelizer never touches them, so
 * `heuristic_overdue` / `citation_document` / `dob_cure_window` / `cure_lapsed`
 * keep their underscores; the KEYS camelize (`compliance_date` →
 * `complianceDate`, `fetched_at` → `fetchedAt`). Both the source and the two
 * cure states are kept OPTIONAL/tolerant of stale persister cache (a legacy
 * payload never carries the new source, so it lands on the heuristic path).
 */
export type ViolationCertAxisState =
  | "overdue"
  | "due_dated"
  | "heuristic_overdue"
  | "due"
  | "cure_lapsed"
  | "cure_open"
  | "none";
export type ViolationCertAxisSource =
  | "citation_document"
  | "dob_cure_window"
  | "heuristic"
  | "none";

export interface ViolationCertAxis {
  state: ViolationCertAxisState;
  /** ISO "YYYY-MM-DD" — present for a dated source (the FDNY citation's
   *  compliance date or the DOB source's cure date); the heuristic never
   *  fabricates a date. */
  complianceDate: string | null;
  source: ViolationCertAxisSource;
  /** When OUR stored copy of the citation was fetched (provenance line). */
  fetchedAt: string | null;
}

/**
 * PR-C3 (CIT-12 lite) — respondent-of-record provenance off the VERIFIED
 * extract. Null unless the D-2 gate + the per-field confidence gate both
 * clear. `respondentOfRecord` is VERBATIM from the document (truncations
 * kept as-printed). `respondentConflict` is the BE's normalized
 * truncation-prefix check — true only on a TRUE mismatch with the city feed.
 */
export interface ViolationRespondentProvenance {
  respondentOfRecord: string;
  serviceAddress: string | null;
  source: "citation_document";
  fetchedAt: string | null;
  respondentConflict: boolean;
}

export interface ViolationMatterDetail extends ViolationMatter {
  /** City issuance date (`violation_date`) — the timeline's first node. Null
   *  when the matter has aged off its building's snapshot. */
  issuedDate: string | null;
  /** Primary charge section (charge_1_code_section). Null when un-sectioned. */
  statute: string | null;
  /** Scanned-citation URL (recorded doc first, else the derived GetViolationImage
   *  link). Null only when there is no ticket to build a link from → the
   *  "View the citation" CTA hides. */
  citationImageUrl: string | null;
  /** Raw inspector narrative (`violation_details`) — the fallback text for an
   *  unmapped/Miscellaneous code. Null when off-snapshot. */
  inspectorNarrative: string | null;
  /** issued → hearing/defaulted → docketed milestones (chronological). */
  timeline: ViolationTimelineEntry[];
  /** PR-C3 cert axis (CIT-9). OPTIONAL for stale-persister tolerance: absent
   *  → the drawer renders exactly its pre-C3 (heuristic) derivation. */
  certAxis?: ViolationCertAxis | null;
  /** PR-C3 respondent provenance (CIT-12 lite). Optional, same tolerance. */
  respondentProvenance?: ViolationRespondentProvenance | null;
}

export interface ViolationMatterDetailResponse {
  buildingId: string;
  matter: ViolationMatterDetail;
}

export const fetchBuildingViolationMatterDetail = (
  buildingId: string,
  matterId: string,
): Promise<ViolationMatterDetailResponse> =>
  api
    .get(
      `tracking/portal/buildings/${buildingId}/violation-matters/${matterId}/`,
    )
    .then((res) => res.data);

export const violationMatterDetailQueryKey = (
  buildingId: string | undefined,
  matterId: string | null | undefined,
) => ["building", buildingId, "violation-matter", matterId] as const;

/**
 * useBuildingViolationMatterDetailQuery — one matter's drawer detail.
 *
 * `enabled` only when BOTH ids are present (the drawer passes `matterId=null`
 * when closed → the query idles). `retry: false` because a 404 (flag off /
 * not-yours) is an expected steady state, not a transient. 5-minute `staleTime`
 * mirrors the sibling matters query so a re-open within a visit is instant.
 */
export const useBuildingViolationMatterDetailQuery = (
  buildingId: string | undefined,
  matterId: string | null | undefined,
) =>
  useQuery<ViolationMatterDetailResponse>({
    queryKey: violationMatterDetailQueryKey(buildingId, matterId),
    enabled: !!buildingId && !!matterId,
    queryFn: () =>
      fetchBuildingViolationMatterDetail(
        buildingId as string,
        matterId as string,
      ),
    staleTime: 1000 * 60 * 5,
    retry: false,
  });

// ---------------------------------------------------------------------------
// FR-14 verdict WRITE seam (PR-4) — one-tap attribution resolve.
//
// `POST /api/tracking/portal/buildings/<id>/violation-matters/<mid>/attribution/`
// body `{ attribution, reason }`. Returns the updated matter + the REFRESHED
// split so the buckets re-render in one round-trip.
//
// ⚠️ Contract note (verified against `portal_views/violation.py` +
// `models_violation.py` 2026-07-29): the endpoint validates `attribution`
// against `ViolationMatter.ATTRIBUTIONS` = {OWNER, TENANT_OR_THIRD_PARTY,
// NEEDS_TRIAGE} AND reads an OPTIONAL `respondent_resolution`. The ONLY
// human-settable resolution is `PREDATES_OWNERSHIP` (anything else → 400); it is
// sent by the "Predates our ownership" one-tap alongside `attribution=
// NEEDS_TRIAGE` (the money stays in the owed hero; the posture derives
// PRIOR_OWNER_VERIFY). Ours / A tenant's send NO resolution.
//
// Request-casing (verified in `api/base.ts`): the axios request interceptor
// runs `decamelizeKeys(config.data)` on every non-FormData body, so the
// camelCase `respondentResolution` key we send is auto-snaked to the
// `respondent_resolution` the view reads. The SCREAMING_SNAKE VALUE
// (`PREDATES_OWNERSHIP`) is a string value, untouched by key-casing.
// ---------------------------------------------------------------------------

/** The attributions the verdict endpoint accepts (== BE `ATTRIBUTIONS`). */
export type AttributionVerdict = ViolationAttribution;

export interface AttributionVerdictResponse {
  buildingId: string;
  matter: ViolationMatter;
  attributionSplit: AttributionSplit;
}

export const postMatterAttribution = (
  buildingId: string,
  matterId: string,
  attribution: AttributionVerdict,
  reason?: string,
  respondentResolution?: ViolationRespondentResolution,
): Promise<AttributionVerdictResponse> => {
  // camelCase key — the axios request interceptor decamelizes it to
  // `respondent_resolution` on the wire. Only include it when set so the common
  // (Ours / A tenant's) verdicts POST the clean `{attribution, reason}` body.
  const body: {
    attribution: AttributionVerdict;
    reason: string;
    respondentResolution?: ViolationRespondentResolution;
  } = { attribution, reason: reason ?? "" };
  if (respondentResolution) body.respondentResolution = respondentResolution;
  return api
    .post(
      `tracking/portal/buildings/${buildingId}/violation-matters/${matterId}/attribution/`,
      body,
    )
    .then((res) => res.data);
};

// ===========================================================================
// PR-5 — portfolio violation dashboard (FR-6 / 6b / 7 / 15b / 15c).
//
// The UNGATED matter-based half of the /portal dashboard. ONE endpoint
// (`GET /api/tracking/portal/portfolio/violation-dashboard/`) returns all five
// composers + the FR-14 attribution split in a single round-trip. Same flag
// contract as the building query: flag OFF → 404 → `data` stays undefined →
// every consuming section renders NOTHING (byte-for-byte no-op).
//
// Wire shape (camelCase axios boundary — `api/base.ts` deep-camelizes EVERY
// response KEY, recursively, including inside arrays; VALUES are untouched).
// Verified against `services_violation_portfolio.py` (serialize_* fns) + the
// `PortalPortfolioViolationDashboardView` wrapper, 2026-07-29:
//   - `this_month`  → `thisMonth`   (`owed_total`→`owedTotal`,
//       `overflow_count`→`overflowCount`; each item `matter_id`→`matterId`,
//       `building_id`→`buildingId`, `ticket_number`→`ticketNumber`,
//       `charge_code`→`chargeCode`, `exposure_usd`→`exposureUsd`; the item
//       `kind` VALUE stays "hearing"/"reopen"/"appeal"; the digest `reason`
//       VALUE stays "paid_no_action"/"sub_100_non_appearance").
//   - `aging.buckets[].key` VALUE stays "90d"/"1yr"/"over_1yr" — it is a FIELD
//       VALUE, not a key, so the camelizer never touches it (`over_1yr` keeps
//       its underscore); `total_usd`→`totalUsd`, `building_ids`→`buildingIds`.
//   - `two_axis`    → `twoAxis`     (`obligations_open`→`obligationsOpen`,
//       `paid_but_uncertified`→`paidButUncertified`).
//   - `dsny_trend`  → `dsnyTrend`   (`total_usd`→`totalUsd`; `trajectory` null).
//   - `ladder_closed` → `ladderClosed` (each item `successor_code`→
//       `successorCode`; `message` VALUE verbatim off the wire).
//   - `attribution_split` → `attributionSplit` (reuses the PR-4 `AttributionSplit`).
// ===========================================================================

export type ThisMonthItemKind = "hearing" | "reopen" | "appeal" | "new";
export type ThisMonthDigestReason = "paid_no_action" | "sub_100_non_appearance";

export interface ViolationDashboardThisMonthItem {
  /**
   * "hearing" (owed/adjudicated) · "reopen"/"appeal" (statutory clocks) · "new"
   * (PR-V3) — a pre-adjudication hearing on a ticket with NO default yet: the
   * "contest now, $0 today -> $X only if defaulted" / "$X scheduled — not yet
   * owed" card. A naive BE exposure<=0 fold used to hide this as "paid".
   */
  kind: ThisMonthItemKind;
  matterId: string;
  buildingId: string;
  ticketNumber: string;
  chargeCode: string;
  /**
   * The building's customer-visible name/address (BE ``_building_label`` —
   * name -> address -> BIN), so the card names WHICH building it links to.
   * Optional on the wire: absent renders no building line (conservative),
   * present renders it (the BE always ships it now).
   */
  buildingLabel?: string | null;
  /**
   * The BE's canonical short code (``_canonical_agency``,
   * ``violation_portfolio.py:428``). Print it. Absent → no chip.
   */
  agency?: string | null;
  /** Event date, ISO "YYYY-MM-DD" | null (hearing date or reopen/appeal deadline). */
  when: string | null;
  /**
   * REOPEN/APPEAL firmness flag (agreed BE contract) — true when the deadline
   * derives from the FEED decision date (an estimate proxy, ~7d after the
   * hearing) rather than the firm mailed decision date, so the strip can render
   * the `~` prefix EXACTLY when the building page's `deriveReopenClockLabel`
   * would (`estimate = !mailed && !!decided`). Camelized from the BE `estimate`
   * key. Optional/absent on non-reopen items and until the BE ships it → treated
   * as `false` (firm), which is the pre-existing conservative render; the strip
   * only OVER-states firmness when this is missing, never under-states.
   */
  estimate?: boolean;
  /** Resolved owed $ (wire Decimal string) | null when off the ledger. */
  exposureUsd: string | null;
  /** PR-3 case-overlay seam — always null on the wire today. */
  engagement: null;
}

export interface ViolationDashboardThisMonthDigest {
  chargeCode: string;
  reason: ThisMonthDigestReason;
  count: number;
}

export interface ViolationDashboardThisMonth {
  asOf: string | null;
  /** DA-R11 — a non-terminal hearing is dated in the recent past (feed lag). */
  stale: boolean;
  owedTotal: string;
  overflowCount: number;
  /** Capped at 5, soonest-first (the BE caps + sorts). */
  items: ViolationDashboardThisMonthItem[];
  digests: ViolationDashboardThisMonthDigest[];
}

/**
 * Aging bucket key VALUES — untouched by the camelizer (field values, not
 * object keys). `over_1yr` keeps its underscore; a 366-day-old matter belongs
 * here (S2 — NOT "4yr+").
 */
export type AgingBucketKey = "90d" | "1yr" | "over_1yr";

export interface ViolationDashboardAgingBucket {
  key: AgingBucketKey;
  /** Display-ready label off the wire (e.g. ">1 year"). Render VERBATIM — the
   *  FE never re-derives the age boundary (S2 / one-formula). */
  label: string;
  count: number;
  totalUsd: string;
  buildingIds: string[];
}

export interface ViolationDashboardAging {
  asOf: string | null;
  totalUsd: string;
  /** Buckets in the BE's order (90d, 1yr, over_1yr) — chronological. */
  buckets: ViolationDashboardAgingBucket[];
}

export interface ViolationDashboardTwoAxis {
  asOf: string | null;
  /** Certification obligations open (the "68"). */
  obligationsOpen: number;
  /** Penalty settled, correction not certified (the "39"). Render verbatim. */
  paidButUncertified: number;
  /**
   * PR-9's CERTIFICATION-FLOOR keys (`certification_floor_keys`), which make
   * the board's "a floor" hedge true: `obligationsOpen` counts rows the feed
   * records as open, and `complianceStatusUnrecorded` counts rows that record
   * NO certification state at all — so the true open count sits in
   * `[obligationsOpen, obligationsOpenCeiling]`.
   *
   * ALL OPTIONAL, and not as defensive padding. `certification_floor_keys`
   * OMITS the three ceiling keys entirely when a coverage read raised
   * (`coverageReadErrors > 0`), precisely so an unmeasured ceiling cannot
   * render as a measured one — it still ships `..._explanation`, so an absent
   * ceiling WITH a reason and an absent ceiling with NO reason (a persisted
   * payload that predates the delta) stay distinguishable.
   */
  rowsConsidered?: number;
  /** Rows recording NO certification state — what we do not know. */
  complianceStatusUnrecorded?: number;
  coverageReadErrors?: number;
  /** `obligationsOpen + complianceStatusUnrecorded`. Absent when unmeasured. */
  obligationsOpenCeiling?: number;
  obligationsOpenCeilingBasis?: { hearingStatus: string; count: number }[];
  obligationsOpenCeilingExplanation?: string;
}

export interface ViolationDashboardDsnyTrend {
  asOf: string | null;
  count: number;
  totalUsd: string;
  message: string;
  /** D4 seam — no historical snapshots yet, so NEVER a trend line. */
  trajectory: null;
}

export interface ViolationDashboardLadderItem {
  matterId: string;
  buildingId: string;
  ticketNumber: string;
  chargeCode: string;
  /** LADDER_CLOSED_MESSAGE off the wire — no specific $ (D5). */
  message: string;
  successorCode: string | null;
}

export interface ViolationDashboardLadderClosed {
  asOf: string | null;
  count: number;
  items: ViolationDashboardLadderItem[];
}

/**
 * PR-V6 — the "One workflow, every agency" per-agency workflow section (screen
 * 7). Each row is an agency the portfolio ACTUALLY has, with its owed $/count, a
 * representative charge, and the DERIVED "Needs" — which get-help professional
 * that agency's remediation typically routes to. VENDOR / LEGAL map to the
 * get-help professional types (a licensed trade / an attorney); PAYMENT is
 * "settle & pay — no professional"; NOT_YOURS is the attribution-excluded
 * verdict (not the owner's matter).
 *
 * One formula (reconciliation): Σ rows.owedUsd == the dashboard's
 * `attributionSplit.owedTotal` (the BE pins this), and `notYoursTotal` reconciles
 * to `attributionSplit.notYours.amount`. The FE reconciles against that EXISTING
 * owed total — never a second one.
 *
 * Camelized from the BE keys (`owed_usd`, `not_yours_usd`, `representative_code`,
 * `dominant_not_yours`, …) by the axios boundary; the need `kind` VALUE
 * ("VENDOR"/"LEGAL"/"PAYMENT"/"NOT_YOURS") is a field value, untouched.
 */
export type AgencyNeedKind = "VENDOR" | "LEGAL" | "PAYMENT" | "NOT_YOURS";

export interface ViolationDashboardAgencyNeed {
  kind: AgencyNeedKind;
  /** Owed matters driving this Need (third-party count for NOT_YOURS). */
  count: number;
}

/**
 * PR-9's `group_row_keys` — the decomposition every GROUP row carries, agency
 * (PR-9) and charge code (PR-10) alike, because both are built by the same
 * `record_group_matter`. ADDITIVE beside the frozen keys, never a redefinition:
 * the identities the BE pins per row AND in aggregate are
 *
 *   `owedUsd`   == `yourEntitiesUsd` + `underReviewUsd`
 *   `owedCount` == `yourEntitiesCount` + `underReviewCount`
 *   `buildingCountWithOwed` <= `buildingCount`
 *
 * `buildingCountBasis` is the one that stops a reader calling a real number a
 * bug: THREE keys named `buildingCount` reach one dashboard payload (this one
 * on the agency row, this one on the charge row, and `attributionSplit`'s
 * portfolio denominator) and a FOURTH lives on the per-code drawer's own
 * endpoint over a wider population. They answer different questions; the basis
 * says which. Render it, never infer it.
 *
 * OPTIONAL for stale-persister tolerance — a payload cached before PR-9 landed
 * carries none of them, and the renderer states the frozen `owedUsd` alone
 * rather than a half-built split.
 */
export interface ViolationDashboardGroupDecomposition {
  yourEntitiesUsd?: string;
  yourEntitiesCount?: number;
  underReviewUsd?: string;
  underReviewCount?: number;
  /** Buildings contributing ANY on-ledger matter to this group. */
  buildingCount?: number;
  /** Which measure `buildingCount` is. `"any_on_ledger_matter"` here. */
  buildingCountBasis?: string;
  /** ...of which contributed OWNER-SIDE dollars. `<= buildingCount`. */
  buildingCountWithOwed?: number;
}

/**
 * PR-9's `group_section_keys` — the section half of the same decomposition,
 * plus `matterCoverage`: how many matters the portfolio holds, how many reached
 * a row, and WHY the rest did not. The board says "45 of 95"; this is the
 * partition behind it, and `matters_total == matters_on_rows + Σ excluded` is
 * an identity the BE pins.
 */
export interface ViolationDashboardCoverageExclusion {
  /** `no_composite_row` | `off_ledger_zero` | `skipped_building` | `error`. */
  reason: string;
  count: number;
  /** Customer copy off the wire. Render VERBATIM — never a second vocabulary. */
  explanation: string;
  /**
   * PR-10, on the UNRECONCILED block's copy of these rows only: were the
   * dollars behind this cause READ (a known $0) or is the read what FAILED.
   * This is the distinction that decides whether the table's total is complete
   * or a floor, and it is why the block carries no dollar of its own.
   */
  dollarsKnown?: boolean;
}

export interface ViolationDashboardMatterCoverage {
  mattersTotal: number;
  mattersOnRows: number;
  excluded: ViolationDashboardCoverageExclusion[];
  /** EVERY swallowed per-matter failure — outside the partition above. */
  matterReadErrors: number;
}

export interface ViolationDashboardGroupSection {
  yourEntitiesTotal?: string;
  underReviewTotal?: string;
  /** `priorOwner` is INSIDE `underReview`; never add the split's own bucket. */
  underReviewIncludesPriorOwner?: boolean;
  matterCoverage?: ViolationDashboardMatterCoverage;
}

export interface ViolationDashboardAgencyRow
  extends ViolationDashboardGroupDecomposition {
  /** Canonical short agency code (BE `_canonical_agency`) — printed, not mapped. */
  agency: string;
  /** Σ owed $ for this agency (wire Decimal string) — reconciles to owedTotal. */
  owedUsd: string;
  owedCount: number;
  /** Σ confirmed-third-party $ for this agency — the "not yours" figure. */
  notYoursUsd: string;
  notYoursCount: number;
  representativeCode: string;
  representativeDescription: string;
  /** True when the agency's dollars are dominantly third-party (the "Not the
   *  owner's case" card). */
  dominantNotYours: boolean;
  /** Short hedged reasoning summary (OPERATIONAL — never legal prose). */
  basis: string;
  /** Derived Needs, count-desc (a single NOT_YOURS need for a not-yours agency). */
  needs: ViolationDashboardAgencyNeed[];
}

export interface ViolationDashboardByAgency
  extends ViolationDashboardGroupSection {
  asOf: string | null;
  /** Σ rows.owedUsd — MUST equal `attributionSplit.owedTotal` (BE-pinned). */
  owedTotal: string;
  /** Σ rows.notYoursUsd — reconciles to `attributionSplit.notYours.amount`. */
  notYoursTotal: string;
  /** Ordered by owed $ desc (not-yours-only agencies, owed $0, at the end). */
  rows: ViolationDashboardAgencyRow[];
}

// ===========================================================================
// PR-10 — the same table keyed on CHARGE CODE (`by_charge`).
//
// `by_charge.owedTotal == by_agency.owedTotal == attributionSplit.owedTotal`
// and `by_charge.notYoursTotal == by_agency.notYoursTotal`, because the two
// tables are ONE pass with two grouping keys (`fold_matters_by_group`) — not
// two summations that happen to agree. Never reconcile one against the other;
// there is nothing to reconcile.
//
// Two rows a code-keyed table needs that an agency-keyed one does not:
//   * the TAIL (`isTail`, `chargeCode === "__tail__"`) — the remainder below
//     `rowCap`, collapsed so the table stays readable. Its dollars ARE in
//     `owedTotal`; it is a display collapse, never a cut.
//   * the UNCODED row (`isUncoded`, `chargeCode === ""`) — the truthful group
//     for tickets the city gave no charge code.
// NEITHER IS DRILLABLE: the per-code drawer answers `__tail__` with a 0-ticket
// panel and rejects `""` with a 400. Suppress the click on both.
// ===========================================================================

export interface ViolationDashboardChargeRow
  extends ViolationDashboardGroupDecomposition {
  /** "" on the uncoded row; `"__tail__"` on the tail. Both non-drillable. */
  chargeCode: string;
  /** Most-common canonical agency for this code ("" on the tail). */
  agency: string;
  owedUsd: string;
  owedCount: number;
  notYoursUsd: string;
  notYoursCount: number;
  representativeDescription: string;
  /** True only when the row carries NO owner-side dollars at all. */
  dominantNotYours: boolean;
  basis: string;
  isTail: boolean;
  /** How many codes the tail collapses. 0 on a named row. */
  codesCollapsed: number;
  isUncoded: boolean;
  needs: ViolationDashboardAgencyNeed[];
}

/**
 * The UNRECONCILED block — what is NOT in the charge table, and why.
 *
 * IT DELIBERATELY CARRIES NO DOLLAR, and a renderer must not mint one. The
 * obvious shape ("portfolio owed − table owed = $X unreconciled") names no
 * cause, cannot be acted on, and is usually $0 on this data — so it would have
 * said nothing while sounding like it said something. Instead every excluded
 * matter travels beside its CAUSE and each cause declares whether the dollars
 * behind it are KNOWN (read, $0) or UNKNOWN (the read failed).
 *
 * `totalsAreComplete` answers "which number is authoritative" PER REQUEST: true
 * when no excluded ticket's dollars are unknown. It is NOT a verdict that every
 * excluded ticket is over — the `no_composite_row` bucket holds live tickets
 * whose lifecycle has not caught up with the ledger — so `basis` MUST render
 * beside it.
 */
export interface ViolationDashboardUnreconciled {
  mattersTotal: number;
  mattersInTable: number;
  mattersNotInTable: number;
  excluded: ViolationDashboardCoverageExclusion[];
  dollarsUnknownMatters: number;
  totalsAreComplete: boolean;
  matterReadErrors: number;
  /** The server's own sentence. Render VERBATIM, and never without it. */
  basis: string;
}

export interface ViolationDashboardByCharge
  extends ViolationDashboardGroupSection {
  asOf: string | null;
  owedTotal: string;
  notYoursTotal: string;
  /** Codes carrying on-ledger dollars at all, BEFORE the cap. */
  codesTotal: number;
  /** How many codes are NAMED before the remainder collapses into the tail. */
  rowCap: number;
  unreconciled: ViolationDashboardUnreconciled;
  rows: ViolationDashboardChargeRow[];
}

// ===========================================================================
// PR-11 — three partitions of ONE owed set, from ONE walk.
//
//   byProcedure.totalUsd == byLifecycle.totalUsd == owedAge.totalUsd
//                        == byAgency.owedTotal == byCharge.owedTotal
//                        == attributionSplit.owedTotal
//
// Three keys over one pass's `owed` branch — the same branch both group tables
// are Σ'd from — so they foot to each other and to the hero with no
// reconciliation step. Summing any of them client-side mints a second formula.
// ===========================================================================

export interface ViolationDashboardPartitionBucket {
  key: string;
  /** Display-ready, off the wire. Render VERBATIM — never re-derive an edge. */
  label: string;
  count: number;
  totalUsd: string;
  /** True for a member under a status the block's vocabulary does not name. */
  outsideDeclaredVocabulary?: boolean;
}

export interface ViolationDashboardProcedureComponent {
  /** One of the EIGHT raw classifier kinds. */
  kind: string;
  label: string;
  count: number;
  totalUsd: string;
  /** Where the charge-pattern panel's own six-row fold would put this kind. */
  fePanelKind: string;
}

/**
 * One of the FOUR folded procedure rows. The 8→4 fold is a DISPLAY COLLAPSE
 * and never a cut: every folded bucket ships its COMPONENT KINDS, each with its
 * own count, its own dollars and its own words, because two of the eight
 * ("other", "new") never had a reopen window and the merged label claims one.
 */
export interface ViolationDashboardProcedureBucket
  extends ViolationDashboardPartitionBucket {
  components: ViolationDashboardProcedureComponent[];
  /** `reopen_available` only: how many carry a FIRM city date. */
  deadlineDatedCount?: number;
}

export interface ViolationDashboardFeFoldDivergence {
  kind: string;
  count: number;
  totalUsd: string;
  bucket: string;
  bucketLabel: string;
  fePanelKind: string;
  fePanelBucket: string;
  explanation: string;
}

export interface ViolationDashboardByProcedure {
  buckets: ViolationDashboardProcedureBucket[];
  totalUsd: string;
  count: number;
  /** Classifier kinds CONSIDERED and found empty — named, never drawn as $0. */
  kindsWithNoMembers: string[];
  foldedFrom: Record<string, string[]>;
  fePanelKinds: string[];
  /** Empty is the MEASURED statement that no matter diverged on this request. */
  feFoldDivergence: ViolationDashboardFeFoldDivergence[];
}

export interface ViolationDashboardByLifecycle {
  buckets: ViolationDashboardPartitionBucket[];
  totalUsd: string;
  count: number;
  statusesWithNoMembers: string[];
}

/** One term of the aging↔owed_age difference, priced and counted. */
export interface ViolationDashboardAgingDiff {
  /** `future_deadline` | `future_hearing` | `unexplained` | `not_yours`. */
  cause: string;
  count: number;
  totalUsd: string;
  explanation: string;
}

/** The identity restated for ONE aging row, with that row's own terms. */
export interface ViolationDashboardAgingBucketRelation {
  agingBucket: string;
  label: string;
  agingCount: number;
  agingTotalUsd: string;
  owedAgeBuckets: string[];
  owedAgeCount: number;
  owedAgeTotalUsd: string;
  inOwedAgeOnly: ViolationDashboardAgingDiff[];
  inAgingBlockOnly: ViolationDashboardAgingDiff[];
  residualUsd: string;
  residualCount: number;
  reconciles: boolean;
}

/**
 * THE COUNTED TWO-WAY IDENTITY against the SHIPPED aging block.
 *
 *   aging.total + Σ(in owed_age only) − Σ(in aging only) == owed_age.total
 *
 * The two populations are NOT NESTED IN EITHER DIRECTION. `owed_age \ aging` is
 * owed matters with a FUTURE date (the aging predicate drops those — they
 * belong to the this-month strip). `aging \ owed_age` is CONFIRMED-THIRD-PARTY
 * matters: `classify_aging_matter` never reads attribution, deliberately,
 * because a ticket whose respondent is a third party still ages on the city's
 * ledger.
 *
 * AND THE SAME IDENTITY PER ROW, which the block total cannot supply. The two
 * blocks draw rows under the SAME edge labels on purpose (the edges ARE the
 * same), so one screen can carry two rows reading "90 days–1 year" over the
 * same tickets at different totals, and neither block-level difference term is
 * the amount that separates them. `byBucket` restates the identity per aging
 * bucket with that bucket's own terms.
 *
 * SO: RENDER THE RELATIONSHIP, OR RENDER ONLY ONE OF THE TWO BLOCKS. Two
 * totals side by side with nothing saying how they differ is the defect four
 * backend PRs were spent preventing.
 */
export interface ViolationDashboardAgingRelationship {
  agingBlockTotalUsd: string;
  agingBlockCount: number;
  identity: string;
  inOwedAgeOnly: ViolationDashboardAgingDiff[];
  inAgingBlockOnly: ViolationDashboardAgingDiff[];
  reconciles: boolean;
  /** PRICED and COUNTED, never a bare boolean. */
  residualUsd: string;
  residualCount: number;
  byBucket: ViolationDashboardAgingBucketRelation[];
  explanation: string;
}

export interface ViolationDashboardOwedAgeBucket
  extends ViolationDashboardPartitionBucket {
  /** Which AGING-block row this row collapses onto — the pairing, on the wire. */
  collapsesTo: string;
  /** `over_3yr` only: how much of the oldest row is an age and how much is a
   *  missing date. A measured 0 IS a fact — the whole owed set was walked. */
  undatedCount?: number;
}

export interface ViolationDashboardOwedAge {
  buckets: ViolationDashboardOwedAgeBucket[];
  totalUsd: string;
  count: number;
  /** Read from the aging block's own constants — never restate an edge. */
  edgeDays: number[];
  bucketsWithNoMembers: string[];
  agingBlockRelationship: ViolationDashboardAgingRelationship;
}

/** What the reference owed total holds and the partition walk did not. */
export interface ViolationDashboardFooting {
  mattersNotPartitioned: number;
  usdNotPartitioned: string;
  complete: boolean;
  /** `by_agency` (the strong form) or `own_pass`. */
  reference: string;
  referenceOwedUsd: string;
  referenceOwedCount: number;
  explanation: string;
}

export interface ViolationDashboardOwedPartitions {
  asOf: string | null;
  owedTotalUsd: string;
  owedCount: number;
  basis: string;
  byProcedure: ViolationDashboardByProcedure;
  byLifecycle: ViolationDashboardByLifecycle;
  owedAge: ViolationDashboardOwedAge;
  footing: ViolationDashboardFooting;
}

export interface ViolationDashboardResponse {
  asOf: string | null;
  thisMonth: ViolationDashboardThisMonth;
  aging: ViolationDashboardAging;
  twoAxis: ViolationDashboardTwoAxis;
  dsnyTrend: ViolationDashboardDsnyTrend;
  ladderClosed: ViolationDashboardLadderClosed;
  /** PR-V6 — the per-agency "one workflow, every agency" section (screen 7). */
  byAgency: ViolationDashboardByAgency;
  /**
   * PR-10 / PR-11. OPTIONAL for the same reason `citationState` is: a payload
   * persisted before those PRs landed carries neither, and the Exposure tab
   * renders the block it has rather than `undefined of undefined`. That
   * tolerance is also why no `CACHE_SCHEMA_VERSION` bump rides with this PR —
   * nothing ALREADY in a persisted payload changed shape or meaning.
   */
  byCharge?: ViolationDashboardByCharge;
  owedPartitions?: ViolationDashboardOwedPartitions;
  /** FR-14 move-never-delete partition (reuses the PR-4 shape). */
  attributionSplit: AttributionSplit;
}

export const fetchPortalViolationDashboard =
  (): Promise<ViolationDashboardResponse> =>
    api
      .get(`tracking/portal/portfolio/violation-dashboard/`)
      .then((res) => res.data);

export const portalViolationDashboardQueryKey = [
  "portal",
  "portfolio",
  "violation-dashboard",
] as const;

/**
 * usePortalViolationDashboardQuery — the ungated dashboard half (PR-5).
 *
 * IDENTICAL flag contract to `useBuildingViolationMattersQuery`: flag OFF →
 * 404 → the query errors, `data` stays undefined, and every PR-5 section (the
 * this-month strip, aging block, two-axis stat, DSNY insight, and the FR-15c
 * ladder note) renders NOTHING. `retry: false` because a 404 is an expected
 * steady state (flag-scoped rollout), not a transient worth hammering. The
 * 5-minute `staleTime` mirrors the sibling building matters query so the two
 * matter-based reads stay coherent within a single visit.
 */
export const usePortalViolationDashboardQuery = () =>
  useQuery<ViolationDashboardResponse>({
    queryKey: portalViolationDashboardQueryKey,
    queryFn: fetchPortalViolationDashboard,
    staleTime: 1000 * 60 * 5,
    retry: false,
  });

// ===========================================================================
// PR-12 — the portfolio VIOLATION RECORD. Two endpoints, one contract.
//
//   GET /portal/portfolio/violation-record/                   the paged list
//   GET /portal/portfolio/violation-record/<matter_id>/documents/   the BODY
//
// THE EMPTY STATE IS THE COMMON CASE ON THIS SURFACE, NOT A FALLBACK. Measured
// by the BE on dev: the LARGEST portfolio in the system is 265 buildings and
// ZERO matters, holding exactly one current OATH capture whose 149 city rows
// were never materialized. A bare `items: []` would read to that customer as a
// clean record, which is false twice over — we have not read most of their
// buildings, and the one we did read is full.
//
// So SEVEN different facts can produce zero rows and each ships its own
// `emptyReason` PLUS a SERVER-OWNED `emptyCopy` sentence. The renderer prints
// the server's sentence and never writes an eighth one: only `no_record` is
// allowed to sound like good news, and the distinction between "nothing was
// found" and "we have not finished reading" is the whole point of the block.
//
// PAGINATION IS KEYSET, NOT OFFSET. `limit` defaults to 20 and is clamped to
// 100 by the SERVICE (not only the view); `cursor` is an opaque base64 keyset
// over `(created_at, id)` minted by the previous page. A matter materialized
// while the customer is paging shifts every offset by one and leaves a keyset
// cursor exact. `nextCursor === null` IS the last page. A malformed cursor
// recovers to page 1 server-side rather than 400ing, so there is no error
// branch to write for one.
//
// ORDER IS `recorded_desc` AND THE WIRE SAYS SO ON PURPOSE: `created_at` is
// when WE recorded the matter, NOT when the city issued it. Never label this
// column an issuance date.
//
// Wire shape (camelCase axios boundary): each item is `serialize_matter`'s
// output — the SAME `ViolationMatter` the building-scoped list ships — plus
// `building_label` → `buildingLabel` and `documents` → `{count, citationState}`.
// The `citationState` VALUE keeps its underscores (`not_fetched`).
// ===========================================================================

/** The record's state. `empty` is the modal outcome, not the error path. */
export type PortfolioRecordState = "has_record" | "empty";

/**
 * Why a record is empty. SEVEN facts, and a customer acts differently on each:
 *
 *   `no_buildings`             the account owns none — the ONE reason whose
 *                              remediation is the customer's.
 *   `buildings_unresolved`     buildings ARE owned, none carries a city BIN we
 *                              can search against. OURS to fix, and the reason
 *                              that reads as the customer's if worded wrong.
 *   `not_materialized`         tracked, but not one current OATH capture.
 *   `capture_not_materialized` we READ the city, the capture holds rows, and
 *                              none became a matter. Capture and materialize
 *                              are separate beats with nothing linking them.
 *   `partial_coverage`         some buildings read, every capture we hold is
 *                              empty. A floor, not a measurement.
 *   `no_record`                every tracked building read, every capture
 *                              EMPTY. THE ONLY ONE THAT MEANS "you are clean".
 *   `module_off`               the product flag is off. Rides the 404 BODY —
 *                              see `fetchPortalViolationRecord`.
 */
export type PortfolioRecordEmptyReason =
  | "no_buildings"
  | "buildings_unresolved"
  | "not_materialized"
  | "capture_not_materialized"
  | "partial_coverage"
  | "no_record"
  | "module_off";

/**
 * The `record` block — shipped on EVERY response, empty or not.
 *
 * EVERY FIGURE IS NULLABLE AND NULL IS NOT ZERO. On the module-off 404 the
 * server ships the SAME KEYS with every figure `null` (`module_off_record_block`)
 * precisely so the renderer has ONE reader and never prints "covers undefined
 * of undefined buildings". A `0` there would read as "you own no buildings".
 */
export interface PortfolioRecordBlock {
  state: PortfolioRecordState;
  /** `null` when there IS a record — a reason for an emptiness that is not
   *  happening would be noise. */
  emptyReason: PortfolioRecordEmptyReason | null;
  /** The server's own sentence, already carrying the as-of date where one is
   *  claimed. Render VERBATIM; never compose a replacement. */
  emptyCopy: string | null;
  /** Buildings this endpoint actually searched. */
  buildingsTotal: number | null;
  /** ...of which hold a current `oath_all_agencies` capture. THE sentence. */
  buildingsWithRecordSource: number | null;
  mattersTotal: number | null;
  /** Buildings the account OWNS. `buildingsOwned - buildingsTotal` is our
   *  BIN-backfill gap, and it is a fact on a full record as much as an empty
   *  one. */
  buildingsOwned: number | null;
  /** Which source coverage was measured against (`oath_all_agencies`). */
  recordSource: string;
  /** The OLDEST covering capture. Coverage counts captures that have aged into
   *  `is_stale`, so a count with no date on it reads as today's clean bill
   *  from a seven-week-old read. Null when nothing has been read. */
  recordSourceAsOf: string | null;
  recordSourceNewestAt: string | null;
  buildingsWithStaleRecordSource: number | null;
  /** Covered buildings whose capture HOLDS ROWS and that have no matter at
   *  all — the capture/materialization gap, as a number so nobody infers it. */
  buildingsWithUnmaterializedRecords: number | null;
}

/**
 * Per-company PROVISIONING for the document pipeline — default OFF, the
 * deliberate fail-closed exception to the flags-default-TRUE house rule.
 *
 * WITHOUT THIS A ROW READING `not_fetched` IS AMBIGUOUS between "the city has
 * no image for this ticket" and "this company was never switched on to go and
 * get it". The wire says which; state it once beside the table.
 */
export interface PortfolioRecordCitationEnrichment {
  enabled: boolean;
  /** `null` when enabled; `not_opted_in` when not. */
  reason: string | null;
}

/**
 * THE BULK-EXPORT CONTRACT, stated rather than left for the FE to guess.
 *
 * `available: false` / `not_supported_portfolio_wide` IS THE ANSWER, and it is
 * on the wire precisely so no portfolio control gets wired to a building-scoped
 * synchronous archive: 95 stored citations across FOURTEEN buildings are
 * 39.2 MB, read fully into memory one at a time and streamed from a gunicorn
 * worker, on a box that has already taken a 20-hour outage from a full disk.
 * READ THE FLAG. If it is false, DRAW NO CONTROL.
 */
export interface PortfolioRecordBulkExport {
  available: boolean;
  reason?: string;
  /** `building` — the scope the two shipped archive endpoints actually serve. */
  scope?: string;
}

/** One ledger row: `serialize_matter` + the building it hangs off + its
 *  document facts. The matter half is the SAME shape the building-scoped list
 *  ships, which is what lets `toRecordRow` derive both surfaces' rows. */
export interface PortfolioRecordMatter extends ViolationMatter {
  /** BE `_building_label` — name → address → BIN. */
  buildingLabel: string;
  documents: {
    /** Owner-visible documents of ANY kind. A counted zero, never absence. */
    count: number;
    citationState: CitationState | null;
  };
}

export interface PortfolioViolationRecordResponse {
  /** `recorded_desc`. ABSENT on the module-off 404 body, which carries only
   *  `record` — so is `limit`, `citationEnrichment` and `bulkExport`. An
   *  absent `bulkExport` draws no control, exactly like a false one. */
  order?: string;
  limit?: number;
  record: PortfolioRecordBlock;
  citationEnrichment?: PortfolioRecordCitationEnrichment;
  bulkExport?: PortfolioRecordBulkExport;
  items: PortfolioRecordMatter[];
  /** Non-zero means `items` is SHORT of the page the query returned. Every
   *  COUNT on the record block still holds (they are DB counts); the ROWS are
   *  a floor and must say so. */
  recordReadErrors?: number;
  /** `null` IS the last page. */
  nextCursor: string | null;
}

/** One document on the LAZY body. */
export interface PortfolioRecordDocument {
  id: string;
  kind: string;
  source: string;
  label: string;
  fetchStatus: string | null;
  fetchedAt: string | null;
  /** `null`, NEVER 0, when we never sized it. A zero-byte document is a
   *  different fact from a document we never measured. */
  byteSize: number | null;
  /** The tenant-scoped serve path, or null. Never a presigned storage URL —
   *  the serve view IS the tenancy boundary. */
  href: string | null;
  /** WHY there is no link: `no_serve_route` (ours to build, not a fetch
   *  failure) | `not_stored` | `fetch_failed`. Null when there IS one. */
  hrefUnavailableReason: string | null;
}

export interface PortfolioRecordDocumentsResponse {
  matterId: string;
  ticketNumber: string;
  buildingId: string;
  buildingLabel: string;
  citationState: CitationState | null;
  documents: PortfolioRecordDocument[];
  documentsTotal: number;
}

/** Page size the list asks for. The SERVICE clamps to [1, 100]; 20 is its own
 *  default and a screen of rows, so it is what this surface asks for. */
export const PORTFOLIO_RECORD_PAGE_SIZE = 20;

/**
 * THE MODULE-OFF 404 CARRIES A BODY, AND THE ERROR PATH IS NOT CAMELIZED.
 *
 * `api/base.ts` installs a response interceptor with a FULFILLED handler only
 * (`api.interceptors.response.use((response) => …)`), so `camelizeKeys` runs on
 * 2xx and NOT on the rejected response an axios error carries. PR-12 put
 * `record.empty_reason = "module_off"` in the 404 body on purpose — "the module
 * is off" and "your record is empty" are different facts a customer acts
 * differently on — and letting the query simply error would throw that body
 * away and land the reader on the generic branch.
 *
 * So the 404 is unwrapped HERE, camelized HERE (one call, the same `humps` the
 * interceptor uses), and returned as a normal result. Everything the 404 body
 * does not carry stays ABSENT rather than being invented: no `bulkExport`
 * (absent draws no control, same as false), no `citationEnrichment`, no
 * `order`. Any other status, and any 404 whose body is not a record block,
 * rethrows.
 */
export const fetchPortalViolationRecord = (params: {
  limit?: number;
  cursor?: string | null;
}): Promise<PortfolioViolationRecordResponse> =>
  api
    .get(`tracking/portal/portfolio/violation-record/`, {
      params: {
        limit: params.limit ?? PORTFOLIO_RECORD_PAGE_SIZE,
        ...(params.cursor ? { cursor: params.cursor } : {}),
      },
    })
    .then((res) => res.data)
    .catch((err: unknown) => {
      const response = (
        err as { response?: { status?: number; data?: unknown } } | undefined
      )?.response;
      if (response?.status !== 404 || !response.data) throw err;
      const body = camelizeKeys(response.data as object) as {
        record?: PortfolioRecordBlock;
      };
      if (!body.record?.emptyReason) throw err;
      return { record: body.record, items: [], nextCursor: null };
    });

export const portalViolationRecordQueryKey = (limit: number) =>
  ["portal", "portfolio", "violation-record", limit] as const;

/**
 * usePortalViolationRecordQuery — the portfolio ledger, page by page.
 *
 * `useInfiniteQuery` BECAUSE THE WIRE IS A KEYSET CURSOR, not because the UI
 * wants infinite scroll: `getNextPageParam` reads `nextCursor` and `null` ends
 * the sequence, which is the endpoint's own contract expressed once. A
 * page-number query would have to mint offsets the endpoint does not accept.
 *
 * `retry: false` — the only expected failure is the flag-off 404, which
 * `fetchPortalViolationRecord` turns into a RESULT rather than an error, so a
 * genuine error here is a real one and hammering it three times helps nobody.
 *
 * `staleTime` matches the sibling violation reads so a tab round-trip inside a
 * visit is instant and the two matter-based reads stay coherent.
 */
export const usePortalViolationRecordQuery = (
  limit: number = PORTFOLIO_RECORD_PAGE_SIZE,
) =>
  useInfiniteQuery<
    PortfolioViolationRecordResponse,
    Error,
    { pages: PortfolioViolationRecordResponse[] },
    ReturnType<typeof portalViolationRecordQueryKey>,
    string | null
  >({
    queryKey: portalViolationRecordQueryKey(limit),
    queryFn: ({ pageParam }) =>
      fetchPortalViolationRecord({ limit, cursor: pageParam }),
    initialPageParam: null,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    staleTime: 1000 * 60 * 5,
    retry: false,
  });

export const fetchPortalRecordMatterDocuments = (
  matterId: string,
): Promise<PortfolioRecordDocumentsResponse> =>
  api
    .get(`tracking/portal/portfolio/violation-record/${matterId}/documents/`)
    .then((res) => res.data);

export const portalRecordDocumentsQueryKey = (
  matterId: string | null | undefined,
) =>
  ["portal", "portfolio", "violation-record", "documents", matterId] as const;

/**
 * usePortalRecordMatterDocumentsQuery — THE LAZY BODY, one expanded row.
 *
 * A SECOND REQUEST, not a hidden field. An accordion caps what is OPEN; it does
 * not cap what was paid for on load, and shipping every document inline so the
 * browser can collapse them has already spent the cost. `enabled` only while a
 * row is expanded, so a collapsed accordion issues nothing.
 */
export const usePortalRecordMatterDocumentsQuery = (
  matterId: string | null | undefined,
) =>
  useQuery<PortfolioRecordDocumentsResponse>({
    queryKey: portalRecordDocumentsQueryKey(matterId),
    enabled: !!matterId,
    queryFn: () => fetchPortalRecordMatterDocuments(matterId as string),
    staleTime: 1000 * 60 * 5,
    retry: false,
  });
